import OpenAI from 'openai';
import { GitLabDirectAccessClient } from './gitlab-direct-access';
import { GitLabError } from './gitlab-error';
import { isResponsesApiModel } from './model-mappings';

import type {
  LanguageModelV2,
  LanguageModelV2CallOptions,
  LanguageModelV2StreamPart,
  LanguageModelV2FinishReason,
  LanguageModelV2CallWarning,
  LanguageModelV2Content,
  LanguageModelV2Usage,
  LanguageModelV2FunctionTool,
  LanguageModelV2Message,
  LanguageModelV2ToolChoice,
} from '@ai-sdk/provider';

export interface GitLabOpenAIConfig {
  provider: string;
  instanceUrl: string;
  getHeaders: () => Record<string, string>;
  fetch?: typeof fetch;
  refreshApiKey?: () => Promise<void>;
  openaiModel?: string;
  maxTokens?: number;
  featureFlags?: {
    DuoAgentPlatformNext: true;
  } & Record<string, boolean>;
  aiGatewayUrl?: string;
  /** Whether to use the Responses API instead of Chat Completions API */
  useResponsesApi?: boolean;
  /**
   * Custom headers for AI Gateway OpenAI proxy requests.
   * Merged with headers from direct_access token response.
   */
  aiGatewayHeaders?: Record<string, string>;
}

export class GitLabOpenAILanguageModel implements LanguageModelV2 {
  readonly specificationVersion = 'v2' as const;
  readonly modelId: string;
  readonly supportedUrls: Record<string, RegExp[]> = {};

  private readonly config: GitLabOpenAIConfig;
  private readonly directAccessClient: GitLabDirectAccessClient;
  private readonly useResponsesApi: boolean;
  private openaiClient: OpenAI | null = null;

  constructor(modelId: string, config: GitLabOpenAIConfig) {
    this.modelId = modelId;
    this.config = config;
    this.useResponsesApi = config.useResponsesApi ?? isResponsesApiModel(modelId);

    this.directAccessClient = new GitLabDirectAccessClient({
      instanceUrl: config.instanceUrl,
      getHeaders: config.getHeaders,
      refreshApiKey: config.refreshApiKey,
      fetch: config.fetch,
      featureFlags: config.featureFlags,
      aiGatewayUrl: config.aiGatewayUrl,
    });
  }

  get provider(): string {
    return this.config.provider;
  }

  private async getOpenAIClient(forceRefresh: boolean = false): Promise<OpenAI> {
    const tokenData = await this.directAccessClient.getDirectAccessToken(forceRefresh);
    const { 'x-api-key': _removed, ...filteredHeaders } = tokenData.headers;

    // Merge: tokenData headers < custom aiGatewayHeaders
    const mergedHeaders = {
      ...filteredHeaders,
      ...this.config.aiGatewayHeaders,
    };

    this.openaiClient = new OpenAI({
      apiKey: tokenData.token,
      baseURL: this.directAccessClient.getOpenAIProxyUrl(),
      defaultHeaders: mergedHeaders,
    });

    return this.openaiClient;
  }

  private isTokenError(error: unknown): boolean {
    if (error instanceof OpenAI.APIError) {
      if (error.status === 401) {
        return true;
      }
      const message = error.message?.toLowerCase() || '';
      if (
        message.includes('token') &&
        (message.includes('expired') || message.includes('revoked') || message.includes('invalid'))
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if an error is a context overflow error (prompt too long)
   * These should NOT trigger token refresh and should be reported to the user.
   */
  private isContextOverflowError(error: unknown): boolean {
    if (error instanceof OpenAI.APIError) {
      // 400 Bad Request with prompt too long message
      if (error.status === 400) {
        const message = error.message?.toLowerCase() || '';
        if (
          message.includes('prompt is too long') ||
          message.includes('prompt too long') ||
          (message.includes('tokens') && message.includes('maximum'))
        ) {
          return true;
        }
      }
    }
    return false;
  }

  private convertTools(
    tools?: LanguageModelV2CallOptions['tools']
  ): OpenAI.ChatCompletionTool[] | undefined {
    if (!tools || tools.length === 0) {
      return undefined;
    }

    return tools
      .filter((tool): tool is LanguageModelV2FunctionTool => tool.type === 'function')
      .map((tool) => {
        const schema = tool.inputSchema as Record<string, unknown>;
        return {
          type: 'function' as const,
          function: {
            name: tool.name,
            description: tool.description || '',
            // Ensure the schema has type: 'object' as OpenAI requires it
            parameters: {
              type: 'object',
              ...schema,
            },
          },
        };
      });
  }

  private convertToolChoice(
    toolChoice?: LanguageModelV2ToolChoice
  ): OpenAI.ChatCompletionToolChoiceOption | undefined {
    if (!toolChoice) {
      return undefined;
    }

    switch (toolChoice.type) {
      case 'auto':
        return 'auto';
      case 'none':
        return 'none';
      case 'required':
        return 'required';
      case 'tool':
        return { type: 'function', function: { name: toolChoice.toolName } };
      default:
        return undefined;
    }
  }

  private convertPrompt(prompt: LanguageModelV2Message[]): OpenAI.ChatCompletionMessageParam[] {
    const messages: OpenAI.ChatCompletionMessageParam[] = [];

    for (const message of prompt) {
      if (message.role === 'system') {
        messages.push({ role: 'system', content: message.content });
        continue;
      }

      if (message.role === 'user') {
        const textParts = message.content
          .filter((part) => part.type === 'text')
          .map((part) => part.text);
        if (textParts.length > 0) {
          messages.push({ role: 'user', content: textParts.join('\n') });
        }
      } else if (message.role === 'assistant') {
        const textParts: string[] = [];
        const toolCalls: OpenAI.ChatCompletionMessageToolCall[] = [];

        for (const part of message.content) {
          if (part.type === 'text') {
            textParts.push(part.text);
          } else if (part.type === 'tool-call') {
            toolCalls.push({
              id: part.toolCallId,
              type: 'function',
              function: {
                name: part.toolName,
                arguments: typeof part.input === 'string' ? part.input : JSON.stringify(part.input),
              },
            });
          }
        }

        const assistantMessage: OpenAI.ChatCompletionAssistantMessageParam = {
          role: 'assistant',
          content: textParts.length > 0 ? textParts.join('\n') : null,
        };
        if (toolCalls.length > 0) {
          assistantMessage.tool_calls = toolCalls;
        }
        messages.push(assistantMessage);
      } else if (message.role === 'tool') {
        for (const part of message.content) {
          if (part.type === 'tool-result') {
            let resultContent: string;
            if (part.output.type === 'text') {
              resultContent = part.output.value;
            } else if (part.output.type === 'json') {
              resultContent = JSON.stringify(part.output.value);
            } else if (part.output.type === 'error-text') {
              resultContent = part.output.value;
            } else if (part.output.type === 'error-json') {
              resultContent = JSON.stringify(part.output.value);
            } else {
              resultContent = JSON.stringify(part.output);
            }
            messages.push({
              role: 'tool',
              tool_call_id: part.toolCallId,
              content: resultContent,
            });
          }
        }
      }
    }

    return messages;
  }

  private convertFinishReason(
    finishReason: string | null | undefined
  ): LanguageModelV2FinishReason {
    switch (finishReason) {
      case 'stop':
        return 'stop';
      case 'length':
        return 'length';
      case 'tool_calls':
        return 'tool-calls';
      case 'content_filter':
        return 'content-filter';
      default:
        return 'unknown';
    }
  }

  /**
   * Convert tools to Responses API format
   */
  private convertToolsForResponses(
    tools?: LanguageModelV2CallOptions['tools']
  ): OpenAI.Responses.FunctionTool[] | undefined {
    if (!tools || tools.length === 0) {
      return undefined;
    }

    return tools
      .filter((tool): tool is LanguageModelV2FunctionTool => tool.type === 'function')
      .map((tool) => {
        // Clone schema and remove $schema field which can confuse the model
        // Zod 4's toJSONSchema() adds "$schema": "https://json-schema.org/draft/2020-12/schema"
        const schema = { ...(tool.inputSchema as Record<string, unknown>) };
        delete schema['$schema'];

        return {
          type: 'function' as const,
          name: tool.name,
          description: tool.description || '',
          parameters: schema,
          strict: false,
        };
      });
  }

  /**
   * Convert prompt to Responses API input format
   */
  private convertPromptForResponses(
    prompt: LanguageModelV2Message[]
  ): OpenAI.Responses.ResponseInput {
    const items: OpenAI.Responses.ResponseInputItem[] = [];

    for (const message of prompt) {
      if (message.role === 'system') {
        // System messages become instructions, handled separately
        continue;
      }

      if (message.role === 'user') {
        const textParts = message.content
          .filter((part) => part.type === 'text')
          .map((part) => part.text);
        if (textParts.length > 0) {
          items.push({
            type: 'message',
            role: 'user',
            content: textParts.map((text) => ({ type: 'input_text' as const, text })),
          } as OpenAI.Responses.ResponseInputItem);
        }
      } else if (message.role === 'assistant') {
        // Handle text content as output message
        const textParts: string[] = [];
        for (const part of message.content) {
          if (part.type === 'text') {
            textParts.push(part.text);
          } else if (part.type === 'tool-call') {
            // Tool calls are function_call items in Responses API
            items.push({
              type: 'function_call',
              call_id: part.toolCallId,
              name: part.toolName,
              arguments: typeof part.input === 'string' ? part.input : JSON.stringify(part.input),
            } as OpenAI.Responses.ResponseInputItem);
          }
        }

        if (textParts.length > 0) {
          items.push({
            type: 'message',
            role: 'assistant',
            content: [{ type: 'output_text', text: textParts.join('\n'), annotations: [] }],
          } as OpenAI.Responses.ResponseInputItem);
        }
      } else if (message.role === 'tool') {
        for (const part of message.content) {
          if (part.type === 'tool-result') {
            let resultContent: string;
            if (part.output.type === 'text') {
              resultContent = part.output.value;
            } else if (part.output.type === 'json') {
              resultContent = JSON.stringify(part.output.value);
            } else if (part.output.type === 'error-text') {
              resultContent = part.output.value;
            } else if (part.output.type === 'error-json') {
              resultContent = JSON.stringify(part.output.value);
            } else {
              resultContent = JSON.stringify(part.output);
            }
            items.push({
              type: 'function_call_output',
              call_id: part.toolCallId,
              output: resultContent,
            } as OpenAI.Responses.ResponseInputItem);
          }
        }
      }
    }

    return items;
  }

  /**
   * Extract system instructions from prompt
   */
  private extractSystemInstructions(prompt: LanguageModelV2Message[]): string | undefined {
    const systemMessages = prompt
      .filter((m) => m.role === 'system')
      .map((m) => m.content)
      .join('\n');
    return systemMessages || undefined;
  }

  /**
   * Convert Responses API status to finish reason
   * Note: Responses API returns 'completed' even when making tool calls,
   * so we need to check the content for tool calls separately.
   */
  private convertResponsesStatus(
    status: string | undefined,
    hasToolCalls: boolean = false
  ): LanguageModelV2FinishReason {
    // If we have tool calls, return 'tool-calls' regardless of status
    if (hasToolCalls) {
      return 'tool-calls';
    }

    switch (status) {
      case 'completed':
        return 'stop';
      case 'incomplete':
        return 'length';
      case 'cancelled':
        return 'stop';
      case 'failed':
        return 'error';
      default:
        return 'unknown';
    }
  }

  async doGenerate(options: LanguageModelV2CallOptions): Promise<{
    content: LanguageModelV2Content[];
    finishReason: LanguageModelV2FinishReason;
    usage: LanguageModelV2Usage;
    warnings: LanguageModelV2CallWarning[];
  }> {
    if (this.useResponsesApi) {
      return this.doGenerateWithResponsesApi(options, false);
    }
    return this.doGenerateWithChatApi(options, false);
  }

  private async doGenerateWithChatApi(
    options: LanguageModelV2CallOptions,
    isRetry: boolean
  ): Promise<{
    content: LanguageModelV2Content[];
    finishReason: LanguageModelV2FinishReason;
    usage: LanguageModelV2Usage;
    warnings: LanguageModelV2CallWarning[];
  }> {
    const client = await this.getOpenAIClient(isRetry);
    const messages = this.convertPrompt(options.prompt);
    const tools = this.convertTools(options.tools);
    const toolChoice =
      options.toolChoice?.type !== 'none' ? this.convertToolChoice(options.toolChoice) : undefined;

    const openaiModel = this.config.openaiModel || 'gpt-4o';
    const maxTokens = options.maxOutputTokens || this.config.maxTokens || 8192;

    try {
      const response = await client.chat.completions.create({
        model: openaiModel,
        max_completion_tokens: maxTokens,
        messages: messages,
        tools: tools,
        tool_choice: tools ? toolChoice : undefined,
        temperature: options.temperature,
        top_p: options.topP,
        stop: options.stopSequences,
      });

      const choice = response.choices[0];
      const content: LanguageModelV2Content[] = [];

      if (choice?.message.content) {
        content.push({ type: 'text', text: choice.message.content });
      }

      if (choice?.message.tool_calls) {
        for (const toolCall of choice.message.tool_calls) {
          if (toolCall.type === 'function') {
            content.push({
              type: 'tool-call',
              toolCallId: toolCall.id,
              toolName: toolCall.function.name,
              input: toolCall.function.arguments,
            });
          }
        }
      }

      const usage: LanguageModelV2Usage = {
        inputTokens: response.usage?.prompt_tokens || 0,
        outputTokens: response.usage?.completion_tokens || 0,
        totalTokens: response.usage?.total_tokens || 0,
      };

      return {
        content,
        finishReason: this.convertFinishReason(choice?.finish_reason),
        usage,
        warnings: [],
      };
    } catch (error) {
      // Check for context overflow FIRST - these should NOT trigger token refresh
      if (this.isContextOverflowError(error)) {
        const apiError = error as InstanceType<typeof OpenAI.APIError>;
        throw new GitLabError({
          message: `Context overflow: ${apiError.message}. Please start a new session or use /compact to reduce context.`,
          statusCode: 400,
          cause: error,
        });
      }

      if (!isRetry && this.isTokenError(error)) {
        this.directAccessClient.invalidateToken();
        return this.doGenerateWithChatApi(options, true);
      }

      if (error instanceof OpenAI.APIError) {
        throw new GitLabError({
          message: `OpenAI API error: ${error.message}`,
          statusCode: error.status,
          cause: error,
        });
      }
      throw error;
    }
  }

  private async doGenerateWithResponsesApi(
    options: LanguageModelV2CallOptions,
    isRetry: boolean
  ): Promise<{
    content: LanguageModelV2Content[];
    finishReason: LanguageModelV2FinishReason;
    usage: LanguageModelV2Usage;
    warnings: LanguageModelV2CallWarning[];
  }> {
    const client = await this.getOpenAIClient(isRetry);
    const input = this.convertPromptForResponses(options.prompt);
    const tools = this.convertToolsForResponses(options.tools);
    const instructions = this.extractSystemInstructions(options.prompt);

    const openaiModel = this.config.openaiModel || 'gpt-5-codex';
    const maxTokens = options.maxOutputTokens || this.config.maxTokens || 8192;

    try {
      const response = await client.responses.create({
        model: openaiModel,
        input,
        instructions,
        tools,
        max_output_tokens: maxTokens,
        temperature: options.temperature,
        top_p: options.topP,
        store: false,
      });

      const content: LanguageModelV2Content[] = [];
      let hasToolCalls = false;

      // Process output items
      for (const item of response.output || []) {
        if (item.type === 'message' && item.role === 'assistant') {
          for (const contentItem of item.content || []) {
            if (contentItem.type === 'output_text') {
              content.push({ type: 'text', text: contentItem.text });
            }
          }
        } else if (item.type === 'function_call') {
          hasToolCalls = true;
          content.push({
            type: 'tool-call',
            toolCallId: item.call_id,
            toolName: item.name,
            input: item.arguments,
          });
        }
      }

      const usage: LanguageModelV2Usage = {
        inputTokens: response.usage?.input_tokens || 0,
        outputTokens: response.usage?.output_tokens || 0,
        totalTokens: response.usage?.total_tokens || 0,
      };

      return {
        content,
        finishReason: this.convertResponsesStatus(response.status, hasToolCalls),
        usage,
        warnings: [],
      };
    } catch (error) {
      // Check for context overflow FIRST - these should NOT trigger token refresh
      if (this.isContextOverflowError(error)) {
        const apiError = error as InstanceType<typeof OpenAI.APIError>;
        throw new GitLabError({
          message: `Context overflow: ${apiError.message}. Please start a new session or use /compact to reduce context.`,
          statusCode: 400,
          cause: error,
        });
      }

      if (!isRetry && this.isTokenError(error)) {
        this.directAccessClient.invalidateToken();
        return this.doGenerateWithResponsesApi(options, true);
      }

      if (error instanceof OpenAI.APIError) {
        throw new GitLabError({
          message: `OpenAI API error: ${error.message}`,
          statusCode: error.status,
          cause: error,
        });
      }
      throw error;
    }
  }

  async doStream(options: LanguageModelV2CallOptions): Promise<{
    stream: ReadableStream<LanguageModelV2StreamPart>;
    request?: { body?: unknown };
    response?: { headers?: Record<string, string> };
  }> {
    if (this.useResponsesApi) {
      return this.doStreamWithResponsesApi(options, false);
    }
    return this.doStreamWithChatApi(options, false);
  }

  private async doStreamWithChatApi(
    options: LanguageModelV2CallOptions,
    isRetry: boolean
  ): Promise<{
    stream: ReadableStream<LanguageModelV2StreamPart>;
    request?: { body?: unknown };
    response?: { headers?: Record<string, string> };
  }> {
    const client = await this.getOpenAIClient(isRetry);
    const messages = this.convertPrompt(options.prompt);
    const tools = this.convertTools(options.tools);
    const toolChoice =
      options.toolChoice?.type !== 'none' ? this.convertToolChoice(options.toolChoice) : undefined;

    const openaiModel = this.config.openaiModel || 'gpt-4o';
    const maxTokens = options.maxOutputTokens || this.config.maxTokens || 8192;

    const requestBody = {
      model: openaiModel,
      max_completion_tokens: maxTokens,
      messages: messages,
      tools: tools,
      tool_choice: tools ? toolChoice : undefined,
      temperature: options.temperature,
      top_p: options.topP,
      stop: options.stopSequences,
      stream: true as const,
      stream_options: { include_usage: true },
    };

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const stream = new ReadableStream<LanguageModelV2StreamPart>({
      start: async (controller) => {
        const toolCalls: Record<number, { id: string; name: string; arguments: string }> = {};

        const usage: LanguageModelV2Usage = {
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0,
        };
        let finishReason: LanguageModelV2FinishReason = 'unknown';
        let textStarted = false;
        const textId = 'text-0';

        try {
          const openaiStream = await client.chat.completions.create({
            ...requestBody,
            stream: true,
          });

          controller.enqueue({ type: 'stream-start', warnings: [] });

          for await (const chunk of openaiStream) {
            const choice = chunk.choices?.[0];

            if (chunk.id && !textStarted) {
              controller.enqueue({
                type: 'response-metadata',
                id: chunk.id,
                modelId: chunk.model,
              });
            }

            if (choice?.delta?.content) {
              if (!textStarted) {
                controller.enqueue({ type: 'text-start', id: textId });
                textStarted = true;
              }
              controller.enqueue({
                type: 'text-delta',
                id: textId,
                delta: choice.delta.content,
              });
            }

            if (choice?.delta?.tool_calls) {
              for (const tc of choice.delta.tool_calls) {
                const idx = tc.index;
                if (!toolCalls[idx]) {
                  toolCalls[idx] = {
                    id: tc.id || '',
                    name: tc.function?.name || '',
                    arguments: '',
                  };
                  controller.enqueue({
                    type: 'tool-input-start',
                    id: toolCalls[idx].id,
                    toolName: toolCalls[idx].name,
                  });
                }
                if (tc.function?.arguments) {
                  toolCalls[idx].arguments += tc.function.arguments;
                  controller.enqueue({
                    type: 'tool-input-delta',
                    id: toolCalls[idx].id,
                    delta: tc.function.arguments,
                  });
                }
              }
            }

            if (choice?.finish_reason) {
              finishReason = self.convertFinishReason(choice.finish_reason);
            }

            if (chunk.usage) {
              usage.inputTokens = chunk.usage.prompt_tokens || 0;
              usage.outputTokens = chunk.usage.completion_tokens || 0;
              usage.totalTokens = chunk.usage.total_tokens || 0;
            }
          }

          if (textStarted) {
            controller.enqueue({ type: 'text-end', id: textId });
          }

          for (const [, tc] of Object.entries(toolCalls)) {
            controller.enqueue({ type: 'tool-input-end', id: tc.id });
            controller.enqueue({
              type: 'tool-call',
              toolCallId: tc.id,
              toolName: tc.name,
              input: tc.arguments || '{}',
            });
          }

          controller.enqueue({ type: 'finish', finishReason, usage });
          controller.close();
        } catch (error) {
          // Check for context overflow FIRST - these should NOT trigger token refresh
          if (self.isContextOverflowError(error)) {
            const apiError = error as InstanceType<typeof OpenAI.APIError>;
            controller.enqueue({
              type: 'error',
              error: new GitLabError({
                message: `Context overflow: ${apiError.message}. Please start a new session or use /compact to reduce context.`,
                statusCode: 400,
                cause: error,
              }),
            });
            controller.close();
            return;
          }

          if (!isRetry && self.isTokenError(error)) {
            self.directAccessClient.invalidateToken();
            controller.enqueue({
              type: 'error',
              error: new GitLabError({ message: 'TOKEN_REFRESH_NEEDED', cause: error }),
            });
            controller.close();
            return;
          }

          if (error instanceof OpenAI.APIError) {
            controller.enqueue({
              type: 'error',
              error: new GitLabError({
                message: `OpenAI API error: ${error.message}`,
                statusCode: error.status,
                cause: error,
              }),
            });
          } else {
            controller.enqueue({ type: 'error', error });
          }
          controller.close();
        }
      },
    });

    return { stream, request: { body: requestBody } };
  }

  private async doStreamWithResponsesApi(
    options: LanguageModelV2CallOptions,
    isRetry: boolean
  ): Promise<{
    stream: ReadableStream<LanguageModelV2StreamPart>;
    request?: { body?: unknown };
    response?: { headers?: Record<string, string> };
  }> {
    const client = await this.getOpenAIClient(isRetry);
    const input = this.convertPromptForResponses(options.prompt);
    const tools = this.convertToolsForResponses(options.tools);
    const instructions = this.extractSystemInstructions(options.prompt);

    const openaiModel = this.config.openaiModel || 'gpt-5-codex';
    const maxTokens = options.maxOutputTokens || this.config.maxTokens || 8192;

    const requestBody = {
      model: openaiModel,
      input,
      instructions,
      tools,
      max_output_tokens: maxTokens,
      temperature: options.temperature,
      top_p: options.topP,
      store: false,
      stream: true as const,
    };

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const stream = new ReadableStream<LanguageModelV2StreamPart>({
      start: async (controller) => {
        // Use output_index as key (like OpenCode does) to track tool calls across events
        const toolCalls: Record<number, { callId: string; name: string; arguments: string }> = {};
        const usage: LanguageModelV2Usage = {
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0,
        };
        let finishReason: LanguageModelV2FinishReason = 'unknown';
        let textStarted = false;
        const textId = 'text-0';

        try {
          const openaiStream = await client.responses.create({
            ...requestBody,
            stream: true,
          });

          controller.enqueue({ type: 'stream-start', warnings: [] });

          for await (const event of openaiStream) {
            // Handle different event types from Responses API streaming
            if (event.type === 'response.created') {
              controller.enqueue({
                type: 'response-metadata',
                id: event.response.id,
                modelId: event.response.model,
              });
            } else if (event.type === 'response.output_item.added') {
              // Track function calls when they start, keyed by output_index
              if (event.item.type === 'function_call') {
                const outputIndex = event.output_index;
                const callId = event.item.call_id;
                toolCalls[outputIndex] = {
                  callId,
                  name: event.item.name,
                  arguments: '',
                };
                controller.enqueue({
                  type: 'tool-input-start',
                  id: callId,
                  toolName: event.item.name,
                });
              }
            } else if (event.type === 'response.output_text.delta') {
              if (!textStarted) {
                controller.enqueue({ type: 'text-start', id: textId });
                textStarted = true;
              }
              controller.enqueue({
                type: 'text-delta',
                id: textId,
                delta: event.delta,
              });
            } else if (event.type === 'response.function_call_arguments.delta') {
              // Use output_index to find the tool call (like OpenCode)
              const outputIndex = event.output_index;
              const tc = toolCalls[outputIndex];
              if (tc) {
                tc.arguments += event.delta;
                controller.enqueue({
                  type: 'tool-input-delta',
                  id: tc.callId,
                  delta: event.delta,
                });
              }
            } else if (event.type === 'response.function_call_arguments.done') {
              const outputIndex = event.output_index;
              const tc = toolCalls[outputIndex];
              if (tc) {
                tc.arguments = event.arguments;
              }
            } else if (event.type === 'response.completed') {
              // Check if there are tool calls to determine finish reason
              const hasToolCalls = Object.keys(toolCalls).length > 0;
              finishReason = self.convertResponsesStatus(event.response.status, hasToolCalls);
              if (event.response.usage) {
                usage.inputTokens = event.response.usage.input_tokens || 0;
                usage.outputTokens = event.response.usage.output_tokens || 0;
                usage.totalTokens = event.response.usage.total_tokens || 0;
              }
            }
          }

          if (textStarted) {
            controller.enqueue({ type: 'text-end', id: textId });
          }

          // Check if we have tool calls to set the correct finish reason
          const hasToolCalls = Object.keys(toolCalls).length > 0;
          if (hasToolCalls && finishReason === 'stop') {
            finishReason = 'tool-calls';
          }

          for (const tc of Object.values(toolCalls)) {
            controller.enqueue({ type: 'tool-input-end', id: tc.callId });
            controller.enqueue({
              type: 'tool-call',
              toolCallId: tc.callId,
              toolName: tc.name,
              input: tc.arguments || '{}',
            });
          }

          controller.enqueue({ type: 'finish', finishReason, usage });
          controller.close();
        } catch (error) {
          // Check for context overflow FIRST - these should NOT trigger token refresh
          if (self.isContextOverflowError(error)) {
            const apiError = error as InstanceType<typeof OpenAI.APIError>;
            controller.enqueue({
              type: 'error',
              error: new GitLabError({
                message: `Context overflow: ${apiError.message}. Please start a new session or use /compact to reduce context.`,
                statusCode: 400,
                cause: error,
              }),
            });
            controller.close();
            return;
          }

          if (!isRetry && self.isTokenError(error)) {
            self.directAccessClient.invalidateToken();
            controller.enqueue({
              type: 'error',
              error: new GitLabError({ message: 'TOKEN_REFRESH_NEEDED', cause: error }),
            });
            controller.close();
            return;
          }

          if (error instanceof OpenAI.APIError) {
            controller.enqueue({
              type: 'error',
              error: new GitLabError({
                message: `OpenAI API error: ${error.message}`,
                statusCode: error.status,
                cause: error,
              }),
            });
          } else {
            controller.enqueue({ type: 'error', error });
          }
          controller.close();
        }
      },
    });

    return { stream, request: { body: requestBody } };
  }
}
